'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';

import { useTrackingUserParamsStore } from '../store';
import { trackStandaloneChange } from '@/modules/googleAnalytics/helpers';

enum TrackingParamKey {
  AppId = 'app_id',
  PwaId = 'pwa_id',
}

const MAIN_PWA_IDENTIFIER = 'lu_main_pwa'; // Should be same as in manifest.json

export const useTrackAppOrPwaId = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const queryAppId = searchParams.get(TrackingParamKey.AppId) ?? undefined;
  const queryPwaId = searchParams.get(TrackingParamKey.PwaId) ?? undefined;

  const { app_id, pwa_id, setTrackingUserParams } = useTrackingUserParamsStore(
    (state) => ({
      app_id: state.app_id,
      pwa_id: state.pwa_id,
      setTrackingUserParams: state.setTrackingUserParams,
    })
  );

  const checkPwaAppOwner = useCallback(
    (isStandalone: boolean) => {
      if (isStandalone) {
        let shouldClean = false;

        if (queryAppId && !app_id) {
          setTrackingUserParams({ app_id: queryAppId });
          shouldClean = true;
        }

        if (queryPwaId && !pwa_id) {
          setTrackingUserParams({ pwa_id: queryPwaId });
          shouldClean = true;
        }

        if (
          !queryAppId &&
          !queryPwaId &&
          !pwa_id &&
          !app_id &&
          typeof document?.referrer !== 'undefined'
        ) {
          const isOwnPwaByReferer =
            document.referrer === '' ||
            document.referrer.includes(window.location.host);
          setTrackingUserParams({
            pwa_id: isOwnPwaByReferer ? MAIN_PWA_IDENTIFIER : document.referrer,
          });
        }

        if (shouldClean) {
          const url = new URL(window.location.href);
          url.searchParams.delete(TrackingParamKey.AppId);
          url.searchParams.delete(TrackingParamKey.PwaId);

          router.replace(url.pathname + url.search);
        }
      } else {
        setTrackingUserParams({ pwa_id: undefined, app_id: undefined });
      }
    },
    [queryAppId, queryPwaId, setTrackingUserParams]
  );

  useEffect(() => {
    const unsubscribe = trackStandaloneChange(checkPwaAppOwner);
    return unsubscribe;
  }, [checkPwaAppOwner]);

  return { app_id, pwa_id };
};
